import {EditorState, type Extension, MapMode, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'

import {getNewlinePrefix, getNewlineSuffix} from '../../language-tools/source-utils'
import {UploadPlaceholderWidget, type UploadPlaceholderWidgetData} from '../widgets/upload-placeholder'
import {createText} from './single-line-editor'

interface UploadPlaceholderSpec {
  id: string
  /** Offset of the placeholder in the document. */
  position: number
  /** If two entries are at the same location, this determines their order. */
  order: number
  data: UploadPlaceholderWidgetData
}

interface UploadPlaceholderUpdate {
  id: string
  data: Partial<UploadPlaceholderWidgetData>
}

type PlaceholderEntry = Omit<UploadPlaceholderSpec, 'id'>

interface PlaceholderFieldState {
  entries: ReadonlyMap<string, PlaceholderEntry>
  decorations: DecorationSet
}

const addUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderSpec>()

const updateUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderUpdate>()

const removeUploadPlaceholderEffect = StateEffect.define<{id: string}>()

export function addUploadPlaceholder(view: EditorView, spec: UploadPlaceholderSpec) {
  view.dispatch({
    effects: [addUploadPlaceholderEffect.of(spec), EditorView.scrollIntoView(spec.position, {y: 'nearest'})],
  })
}

export function updateUploadPlaceholder(view: EditorView, update: UploadPlaceholderUpdate) {
  view.dispatch({effects: [updateUploadPlaceholderEffect.of(update)]})
}

/** Remove the placeholder from the view and replace with the `insert` text. Has no effect if the placeholder has been deleted. */
export function replaceUploadPlaceholder(view: EditorView, id: string, insert: string) {
  const position = view.state.field(uploadPlaceholderField).entries.get(id)?.position ?? null
  // location will be null if the placeholder has been deleted
  if (position === null) return

  const doc = view.state.doc.toString()
  const newlineBefore = getNewlinePrefix(doc, position)
  const newlineAfter = getNewlineSuffix(doc, position)

  // Note: we intentionally do not scroll into view in this case; the upload may complete outside of the current view
  // and scrolling would be disruptive
  view.dispatch({
    effects: [removeUploadPlaceholderEffect.of({id})],
    changes: {from: position, insert: createText(`${newlineBefore}${insert}${newlineAfter}`)},
  })
}

export function getAllEntries(state: EditorState): UploadPlaceholderSpec[] {
  const field = state.field(uploadPlaceholderField)
  return Array.from(field.entries.entries()).map(([id, entry]) => ({
    id,
    ...entry,
  }))
}

const uploadPlaceholderField = StateField.define<PlaceholderFieldState>({
  create: () => ({entries: new Map(), decorations: Decoration.none}),
  update: (state, tr) => {
    const entries = new Map(state.entries)
    let changed = false
    const rightAssocEntries = new Set<string>()

    for (const effect of tr.effects) {
      if (effect.is(addUploadPlaceholderEffect)) {
        const {id, ...entry} = effect.value
        entries.set(id, entry)
        changed = true
      } else if (effect.is(updateUploadPlaceholderEffect) && entries.has(effect.value.id)) {
        const existing = entries.get(effect.value.id)!
        entries.set(effect.value.id, {
          ...existing,
          data: {...existing.data, ...effect.value.data},
        })
        changed = true
      } else if (effect.is(removeUploadPlaceholderEffect) && entries.has(effect.value.id)) {
        const deletedEntry = entries.get(effect.value.id)!
        entries.delete(effect.value.id)

        // Special case: if multiple entries are at the same location and a middle one gets replaced, all the ones
        // before it need to be associated with the previous char (the default) and all the ones after it need to be
        // associated with the next char so that they get pushed to the right
        for (const [id, entry] of entries)
          if (entry.position === deletedEntry.position && entry.order > deletedEntry.order) rightAssocEntries.add(id)

        changed = true
      }
    }

    if (tr.docChanged && entries.size > 0) {
      for (const [id, entry] of entries) {
        const newPosition = tr.changes.mapPos(entry.position, rightAssocEntries.has(id) ? 1 : -1, MapMode.TrackDel)

        if (newPosition !== entry.position) {
          // If the location is `null`, the placeholder has been deleted and so we forget about it
          if (newPosition === null) {
            entries.delete(id)
          } else {
            entries.set(id, {
              ...entry,
              position: newPosition,
            })
          }

          changed = true
        }
      }
    }

    if (!changed) return state

    // Rebuild decorations from scratch for all entries
    const builder = new RangeSetBuilder<Decoration>()
    const sortedEntries = Array.from(entries.values()).sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position
      return a.order - b.order
    })

    for (const entry of sortedEntries) {
      const decoration = Decoration.replace({
        widget: new UploadPlaceholderWidget(entry.data),
      })
      // Ensure correct order for multiple decorations at the same location
      decoration.startSide = entry.order
      builder.add(entry.position, entry.position, decoration)
    }
    const decorations = builder.finish()

    return {
      entries,
      decorations,
    }
  },
  provide: field => EditorView.decorations.from(field, value => value.decorations),
})

export const uploadPlaceholderExtension: Extension = [uploadPlaceholderField]
